import { useState, useEffect } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { useNativeInputSync } from "../hooks/use-native-input-sync"
import { Input } from "../components/ui/input"
import { Calendar as CalendarComponent } from "../components/ui/calendar"
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover"
import { Button } from "../components/ui/button"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"
import { Search, CalendarIcon } from "lucide-react"
import { format, parse, isValid } from "date-fns"

/** Extract last segment from a bind path. E.g., "#{form.email}" -> "email" */
function extractFieldName(bind?: string): string | undefined {
  if (!bind) return undefined
  const stripped = bind.replace(/^#\{/, "").replace(/\}$/, "")
  const segments = stripped.split(".")
  return segments[segments.length - 1]
}

function DateInputPopover({
  element,
  boundValue,
  placeholder,
  bind,
}: {
  element: { id: string; className?: string }
  boundValue: string
  placeholder: string
  bind?: string
}) {
  const { dispatchEvent } = useRenderContext()
  const [open, setOpen] = useState(false)
  const [localDate, setLocalDate] = useState<Date | undefined>(undefined)

  // Sync local state from bound state (date format conversion -- separate concern from state sync)
  useEffect(() => {
    if (boundValue) {
      const parsed = parse(boundValue, "yyyy-MM-dd", new Date())
      if (isValid(parsed)) {
        setLocalDate(parsed)
      }
    } else {
      setLocalDate(undefined)
    }
  }, [boundValue])

  const handleSelect = (date: Date | undefined) => {
    setLocalDate(date)
    const value = date ? format(date, "yyyy-MM-dd") : ""
    dispatchEvent(element.id, "change", { value })
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* base-ui uses `render` for composition; the original `asChild` pattern
          was a Radix leftover. Button is rendered as-is with merged trigger props. */}
      <PopoverTrigger
        data-empty={!localDate}
        data-testid={`input-${element.id}`}
        data-action="input"
        data-field-name={extractFieldName(bind)}
        data-field-type="date"
        data-atom-type="input"
        render={
          <Button
            variant="outline"
            className={cn(
              "justify-start text-left font-normal data-[empty=true]:text-muted-foreground",
              element.className,
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
            {localDate ? format(localDate, "yyyy-MM-dd") : placeholder}
          </Button>
        }
      />
      <PopoverContent className="w-auto p-0" align="start">
        <CalendarComponent
          mode="single"
          selected={localDate}
          onSelect={handleSelect}
        />
      </PopoverContent>
    </Popover>
  )
}

export function InputRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("input")
  const resolvedVariant = (element as { variant?: string }).variant
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const placeholderRaw = element.placeholder ?? (element as any).hint
  const placeholder = placeholderRaw
    ? String(resolveSymbol(placeholderRaw) ?? "")
    : undefined

  // State binding via useBoundValue (1-cycle sync, no useEffect delay)
  const [boundValue, setBoundValue] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    "",
    String,
  )

  // Native DOM input event listener -- catches Playwright page.fill()/page.type()
  // which may not trigger React's synthetic onChange.
  const nativeRef = useNativeInputSync((value) => {
    setBoundValue(value)
    dispatchEvent(element.id, "change", { value })
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBoundValue(e.target.value)
    dispatchEvent(element.id, "change", { value: e.target.value })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Standard DOM "keydown" event dispatch. Blocks bind "<id>:keydown" and
    // branch on #{event.key === 'Enter'} / 'Escape' / 'Tab' inside the flow.
    // No hardcoded ':enter' / ':escape' event names — keep the runtime
    // dispatch aligned with the event-types ontology.
    dispatchEvent(element.id, "keydown", {
      key: e.key,
      metaKey: e.metaKey,
      ctrlKey: e.ctrlKey,
      shiftKey: e.shiftKey,
      altKey: e.altKey,
      value: e.currentTarget.value,
    })
  }

  const declaredType = (element.inputType as string) ?? "text"
  const isDateType = ["date", "month", "datetime-local"].includes(declaredType)
  const inputType = isDateType ? "text" : declaredType
  const required = element.required === true || element.required === "true"
  const disabled = element.disabled === true || element.disabled === "true"
  const readOnly = element.readonly === true
  const pattern = element.pattern as string | undefined
  const min = element.min as number | undefined
  const max = element.max as number | undefined
  const step = element.step as number | undefined

  const datePlaceholder = isDateType
    ? (placeholder || (declaredType === "month" ? "YYYY-MM" : "YYYY-MM-DD"))
    : placeholder

  const inputProps = {
    type: inputType,
    placeholder: datePlaceholder,
    required: required || undefined,
    disabled: disabled || undefined,
    readOnly: readOnly || undefined,
    pattern: pattern || undefined,
    min: min,
    max: max,
    step: step,
    ...(element.bind !== undefined
      ? { value: boundValue }
      : (element as any).defaultValue !== undefined
        ? { defaultValue: String(resolveSymbol((element as any).defaultValue) ?? "") }
        : (element as any).value !== undefined
          ? { defaultValue: String(resolveSymbol((element as any).value) ?? (element as any).value) }
          : {}),
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    "data-testid": `input-${element.id}`,
    "data-action": "input" as const,
    "data-field-name": extractFieldName(element.bind),
    "data-field-type": declaredType,
    "data-atom-type": "input" as const,
    "data-variant": resolvedVariant,
    "data-size": resolvedSize,
  }

  // Date input: shadcn Calendar in Popover (separate component for hooks)
  if (isDateType && declaredType === "date") {
    return (
      <DateInputPopover
        element={element}
        boundValue={boundValue ?? ""}
        placeholder={datePlaceholder ?? "YYYY-MM-DD"}
        bind={element.bind}
      />
    )
  }

  // Render with leading icon (e.g., search)
  if (element.icon === "search") {
    return (
      <div className="relative w-full" data-atom-type="input">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={nativeRef}
          className={cn("pl-9", stripPresetConflicts(element.className, 'input'))}
          {...inputProps}
        />
      </div>
    )
  }

  return (
    <Input
      ref={nativeRef}
      className={stripPresetConflicts(element.className, 'input')}
      {...inputProps}
    />
  )
}
